import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from 'src/lib/supabase';
import Table from './shared/Table';
import KpiStrip from './shared/KpiStrip';
import Drawer from './shared/Drawer';
import Badge from './shared/Badge';
import Modal from './shared/Modal';
import Pagination from './shared/Pagination';

const UI = {
  spacing: { xs: 6, sm: 8, md: 12, lg: 16, xl: 24 },
  radius: { sm: 6, md: 8, lg: 12 },
  colors: {
    primary: "#0F172A",
    secondary: "#334155",
    muted: "#64748B",
    border: "#E2E8F0",
    bg: "#F8FAFC",
    success: "#059669",
    warning: "#D97706",
    danger: "#DC2626",
    info: "#3B82F6"
  }
};

const PAGE_SIZE = 10;

const AdminPayments = () => {
  // Safe destructuring with fallbacks
  const context = useOutletContext() || {};
  const { 
    showToast = () => {}, 
    adminRole = null, 
    hasPermission = () => false 
  } = context;
  
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeKpi, setActiveKpi] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState(-1);
  const [currentPage, setCurrentPage] = useState(1);
  const [showInitiateModal, setShowInitiateModal] = useState(false);
  const [newPayment, setNewPayment] = useState({ phone: '', amount: '', jobId: '' });
  const [jobs, setJobs] = useState([]);
  const searchTimeoutRef = useRef();

  useEffect(() => {
    loadPayments();
    loadJobs();
    
    // Subscribe to realtime payment updates
    const subscription = supabase
      .channel('payments_channel')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'job_requests' },
        () => {
          loadPayments();
        }
      )
      .subscribe();
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('job_requests')
        .select(`
          id, description, estimated_price, payment_status, platform_fee,
          mpesa_receipt_number, mpesa_checkout_request_id, created_at,
          customer:customer_id ( full_name ),
          pro:pro_id ( full_name )
        `)
        .order(sortField === 'created_at' ? 'created_at' : 'created_at', { ascending: sortDir === 1 });

      if (searchQuery) {
        query = query.or(`mpesa_receipt_number.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      const shaped = (data || []).map(j => {
        const amount = j.estimated_price || 0;
        // platform_fee is only ever real once a payment is confirmed
        // (set by mpesa-callback at that moment) — never estimated here.
        const fee = j.payment_status === 'paid' ? (j.platform_fee ?? 0) : null;
        return {
          id: j.id,
          transaction_id: j.mpesa_receipt_number || '—',
          job: { job_ref: j.description || 'Booking', customer: { name: j.customer?.full_name }, pro: { name: j.pro?.full_name } },
          amount,
          platform_fee: fee,
          payout_amount: fee !== null ? amount - fee : null,
          method: 'mpesa',
          status: j.payment_status,
          created_at: j.created_at,
          _checkoutRequestId: j.mpesa_checkout_request_id,
        };
      });

      setPayments(shaped);
    } catch (error) {
      console.error('Error loading payments:', error);
      showToast('Failed to load payments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadJobs = async () => {
    const { data } = await supabase
      .from('job_requests')
      .select('id, description, estimated_price, customer:customer_id ( phone )')
      .eq('payment_status', 'unpaid');
    setJobs((data || []).map(j => ({
      id: j.id,
      job_ref: j.description || 'Booking',
      price: j.estimated_price,
      customerPhone: j.customer?.phone || '',
    })));
  };

  const handleSearch = useCallback((value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      loadPayments();
    }, 300);
  }, []);

  const handleRefund = async (paymentId) => {
    if (!hasPermission('can_refund')) {
      showToast('You don\'t have permission to process refunds', 'error');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('mpesa-refund', {
        body: { booking_id: paymentId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      showToast('Refund initiated — awaiting M-Pesa confirmation');
      loadPayments();
      setSelectedPayment(null);
    } catch (error) {
      console.error('Error processing refund:', error);
      showToast(`Failed to process refund: ${error.message}`, 'error');
    }
  };

  const handleInitiatePayment = async () => {
    try {
      // mpesa-initiate reads the real amount server-side from job_requests
      // itself (see Day 3-4) — it only needs booking_id and phone, and
      // deliberately ignores any client-supplied amount for security.
      const { data, error } = await supabase.functions.invoke('mpesa-initiate', {
        body: { booking_id: newPayment.jobId, phone: newPayment.phone },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      showToast('STK push sent to customer');
      setShowInitiateModal(false);
      setNewPayment({ phone: '', amount: '', jobId: '' });
      loadPayments();
    } catch (error) {
      console.error('Error initiating payment:', error);
      showToast(`Failed to initiate payment: ${error.message}`, 'error');
    }
  };

  const filteredPayments = useMemo(() => {
    let filtered = [...payments];
    
    if (activeKpi !== 'all') {
      filtered = filtered.filter(payment => payment.status === activeKpi);
    }
    
    return filtered;
  }, [payments, activeKpi]);

  const paginatedPayments = filteredPayments.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const totalPages = Math.ceil(filteredPayments.length / PAGE_SIZE);

  const totalRevenue = payments.reduce((sum, p) => sum + (p.status === 'completed' ? p.amount : 0), 0);
  const totalFees = payments.reduce((sum, p) => sum + (p.status === 'completed' ? p.platform_fee : 0), 0);
  const pendingCount = payments.filter(p => p.status === 'pending').length;
  const failedCount = payments.filter(p => p.status === 'failed').length;
  const refundedCount = payments.filter(p => p.status === 'refunded').length;

  const kpiCards = [
    { key: 'all', label: 'Total Revenue', value: `KES ${(totalRevenue / 1000).toFixed(0)}K`, delta: 18 },
    { key: 'fees', label: 'Platform Fees', value: `KES ${(totalFees / 1000).toFixed(0)}K`, delta: 18 },
    { key: 'pending', label: 'Pending', value: pendingCount, delta: -5 },
    { key: 'completed', label: 'Completed', value: payments.filter(p => p.status === 'completed').length, delta: 22 },
    { key: 'failed', label: 'Failed', value: failedCount, delta: -8 },
    { key: 'refunded', label: 'Refunded', value: refundedCount, delta: 12 }
  ];

  const columns = [
    { key: 'transaction_id', label: 'Transaction ID', width: 140 },
    { 
      key: 'job', 
      label: 'Job Ref', 
      width: 120,
      render: (val) => val?.job_ref || '—'
    },
    { 
      key: 'amount', 
      label: 'Amount', 
      width: 120, 
      align: 'right',
      render: (val) => `KES ${val?.toLocaleString()}`
    },
    { 
      key: 'platform_fee', 
      label: 'Fee', 
      width: 100, 
      align: 'right',
      render: (val) => `KES ${val?.toLocaleString()}`
    },
    { 
      key: 'method', 
      label: 'Method', 
      width: 100,
      render: (val) => val?.toUpperCase()
    },
    { 
      key: 'status', 
      label: 'Status', 
      width: 120,
      render: (val) => (
        <Badge variant={val === 'completed' ? 'success' : val === 'failed' ? 'danger' : val === 'refunded' ? 'warning' : 'default'}>
          {val}
        </Badge>
      )
    },
    { 
      key: 'created_at', 
      label: 'Date', 
      width: 140,
      render: (val) => new Date(val).toLocaleString()
    }
  ];

  return (
    <div>
      {/* Header Actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: UI.spacing.xl,
        flexWrap: 'wrap',
        gap: UI.spacing.md
      }}>
        <input
          type="text"
          placeholder="Search by transaction ID, job ref..."
          onChange={(e) => handleSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: 200,
            maxWidth: 300,
            padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
            border: `1px solid ${UI.colors.border}`,
            borderRadius: UI.radius.md,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            outline: 'none'
          }}
        />
        
        <button
          onClick={() => setShowInitiateModal(true)}
          style={{
            padding: `${UI.spacing.sm}px ${UI.spacing.lg}px`,
            background: UI.colors.success,
            color: UI.colors.white,
            border: 'none',
            borderRadius: UI.radius.md,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          + Initiate Payment
        </button>
      </div>

      {/* KPI Strip */}
      <KpiStrip
        cards={kpiCards}
        activeKey={activeKpi}
        onCardClick={(key) => {
          setActiveKpi(key);
          setCurrentPage(1);
        }}
      />

      {/* Payments Table */}
      <Table
        columns={columns}
        data={paginatedPayments}
        loading={loading}
        onRowClick={(payment) => setSelectedPayment(payment)}
        selectable={false}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={filteredPayments.length}
          pageSize={PAGE_SIZE}
        />
      )}

      {/* Payment Drawer */}
      <Drawer
        isOpen={!!selectedPayment}
        onClose={() => setSelectedPayment(null)}
        title="Payment Details"
        footer={
          selectedPayment?.status === 'completed' && hasPermission('can_refund') && (
            <button
              onClick={() => handleRefund(selectedPayment.id)}
              style={{
                width: '100%',
                padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
                background: UI.colors.warning,
                color: UI.colors.white,
                border: 'none',
                borderRadius: UI.radius.md,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Process Refund
            </button>
          )
        }
      >
        {selectedPayment && (
          <div>
            <div style={{ marginBottom: UI.spacing.lg }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: UI.colors.muted, marginBottom: UI.spacing.xs }}>
                Transaction ID
              </div>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, fontWeight: 600, color: UI.colors.primary }}>
                {selectedPayment.transaction_id || '—'}
              </div>
            </div>
            
            <div style={{ marginBottom: UI.spacing.lg }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: UI.colors.muted, marginBottom: UI.spacing.xs }}>
                Job Reference
              </div>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, color: UI.colors.secondary }}>
                {selectedPayment.job?.job_ref}
              </div>
            </div>
            
            <div style={{ marginBottom: UI.spacing.lg }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: UI.colors.muted, marginBottom: UI.spacing.xs }}>
                Customer
              </div>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, color: UI.colors.secondary }}>
                {selectedPayment.job?.customer?.name}
              </div>
            </div>
            
            <div style={{ marginBottom: UI.spacing.lg }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: UI.colors.muted, marginBottom: UI.spacing.xs }}>
                Service Pro
              </div>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, color: UI.colors.secondary }}>
                {selectedPayment.job?.pro?.name}
              </div>
            </div>
            
            <div style={{ marginBottom: UI.spacing.lg }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: UI.colors.muted, marginBottom: UI.spacing.xs }}>
                Amount
              </div>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 24, fontWeight: 800, color: UI.colors.primary }}>
                KES {selectedPayment.amount?.toLocaleString()}
              </div>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: UI.colors.muted, marginTop: UI.spacing.xs }}>
                Platform Fee: KES {selectedPayment.platform_fee?.toLocaleString()}
              </div>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: UI.colors.muted }}>
                Payout Amount: KES {selectedPayment.payout_amount?.toLocaleString()}
              </div>
            </div>
            
            <div>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: UI.colors.muted, marginBottom: UI.spacing.xs }}>
                Status
              </div>
              <Badge variant={selectedPayment.status === 'completed' ? 'success' : selectedPayment.status === 'failed' ? 'danger' : 'default'}>
                {selectedPayment.status}
              </Badge>
            </div>
          </div>
        )}
      </Drawer>

      {/* Initiate Payment Modal */}
      <Modal
        isOpen={showInitiateModal}
        onClose={() => setShowInitiateModal(false)}
        title="Initiate Payment"
      >
        <div style={{ marginBottom: UI.spacing.lg }}>
          <label style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 12,
            fontWeight: 600,
            color: UI.colors.secondary,
            display: 'block',
            marginBottom: UI.spacing.sm
          }}>
            Job
          </label>
          <select
            value={newPayment.jobId}
            onChange={(e) => {
              const job = jobs.find(j => j.id === e.target.value);
              setNewPayment(prev => ({ ...prev, jobId: e.target.value, amount: job?.price || '', phone: job?.customerPhone || prev.phone }));
            }}
            style={{
              width: '100%',
              padding: UI.spacing.sm,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13
            }}
          >
            <option value="">Select a job</option>
            {jobs.map(job => (
              <option key={job.id} value={job.id}>
                {job.job_ref} - KES {job.price?.toLocaleString()}
              </option>
            ))}
          </select>
        </div>
        
        <div style={{ marginBottom: UI.spacing.lg }}>
          <label style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 12,
            fontWeight: 600,
            color: UI.colors.secondary,
            display: 'block',
            marginBottom: UI.spacing.sm
          }}>
            Phone Number
          </label>
          <input
            type="tel"
            value={newPayment.phone}
            onChange={(e) => setNewPayment(prev => ({ ...prev, phone: e.target.value }))}
            placeholder="0712345678"
            style={{
              width: '100%',
              padding: UI.spacing.sm,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13
            }}
          />
        </div>
        
        <div style={{ marginBottom: UI.spacing.lg }}>
          <label style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 12,
            fontWeight: 600,
            color: UI.colors.secondary,
            display: 'block',
            marginBottom: UI.spacing.sm
          }}>
            Amount (KES)
          </label>
          <input
            type="number"
            value={newPayment.amount}
            readOnly
            style={{
              width: '100%',
              padding: UI.spacing.sm,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              background: UI.colors.bg
            }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: UI.spacing.md }}>
          <button
            onClick={handleInitiatePayment}
            disabled={!newPayment.jobId || !newPayment.phone}
            style={{
              flex: 1,
              padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
              background: UI.colors.success,
              color: UI.colors.white,
              border: 'none',
              borderRadius: UI.radius.md,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              opacity: (!newPayment.jobId || !newPayment.phone) ? 0.5 : 1
            }}
          >
            Send STK Push
          </button>
          <button
            onClick={() => setShowInitiateModal(false)}
            style={{
              flex: 1,
              padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
              background: UI.colors.white,
              color: UI.colors.secondary,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPayments;